/* ---------- CP-002's pump feedback through the delta contactor's closer (CP-002 review 13.1, #3745) ----------
 * The logic relay's feedback input In now reads -QAn2's closer through -QAn3's closer 13-14 (control.ts), so a pump left in star by a
 * cut delta coil wire is caught. A save made before holds page 4 drawn, and the panel wired, with the input straight on the running
 * net, which the review and the wiring step would now call wrong. How such a save is read: the save migration in packages/progress
 * uses this (format 34). Self-contained on purpose, as breakers.ts: the migration imports this file alone, not CP-002's pack. */

/** Pump n's feedback: the logic relay's input, the delta contactor's closer, and page 4's element for it. */
export const CP002_DELTA_FEEDBACK = ([1, 2] as const).map(n => ({ n, input: `KF1:I${n}`, a: `QA${n}3:13`, b: `QA${n}3:14`, part: `deltaRuns${n}` }));
/** Page 4's middle contact row (pages.ts ROW.b), where the closer stands above the input, its link clear of its tag. */
const ROW_B = 230;

/** The wires with each input that is not yet fed by the delta closer moved onto the closer's 13, and the closer's 14 to the input
 *  added right after it. `pair` reads and writes a wire's two ends, so drawn pairs and built conductors take the same rule. */
function rewire<W>(wires: W[], pair: { ends: (w: W) => [unknown, unknown]; with: (w: W, a: string, b: string) => W; make: (a: string, b: string) => W },
  pumps: typeof CP002_DELTA_FEEDBACK): W[] {
  let out = wires;
  for (const f of pumps) {
    const fed = out.some(w => { const [x, y] = pair.ends(w); return (x === f.b && y === f.input) || (x === f.input && y === f.b); });
    if (fed) continue;
    const k = out.findIndex(w => pair.ends(w).includes(f.input)); if (k < 0) continue;
    const [x, y] = pair.ends(out[k]), moved = x === f.input ? pair.with(out[k], f.a, y as string) : pair.with(out[k], x as string, f.a);
    out = [...out.slice(0, k), moved, pair.make(f.b, f.input), ...out.slice(k + 1)];
  }
  return out;
}

/** Page 4 as drawn: the closer placed above pump 1's input where the input is placed, and the input's wire through it. Pump 2's
 *  signals are drawn ready by the page itself, not saved. */
function page4(d: any): any {
  if (!d || typeof d !== "object") return d;
  const parts = d.parts && typeof d.parts === "object" ? d.parts : {}, wires: any[] = Array.isArray(d.wires) ? d.wires : [];
  const pumps = CP002_DELTA_FEEDBACK.filter(f => f.n === 1);
  const placed = parts.feedback1 && !parts.deltaRuns1 ? { ...parts, deltaRuns1: { x: parts.feedback1.x, y: ROW_B } } : parts;
  const next = rewire(wires, { ends: w => [w?.[0], w?.[1]], with: (_w, a, b) => [a, b], make: (a, b) => [a, b] }, pumps);
  return placed === parts && next === wires ? d : { ...d, parts: placed, wires: next };
}

/** A build record ({ placements, wires }) with each input's conductor landed on the closer's 13 instead (its size, colour and mark
 *  kept) and the closer's 14 to the input added; the wiring step gives the new one the list's size, colour and mark when it loads. A
 *  delta contactor not placed yet keeps its wires. */
function build(b: any): any {
  const wires: any[] = Array.isArray(b?.wires) ? b.wires : [], placed = new Set((Array.isArray(b?.placements) ? b.placements : []).map((p: any) => p?.tag));
  const pumps = CP002_DELTA_FEEDBACK.filter(f => placed.has(f.a.split(":")[0]));
  const next = rewire(wires, { ends: w => [w?.from, w?.to], with: (w, a, c) => ({ ...w, from: a, to: c }), make: (a, c) => ({ from: a, to: c }) }, pumps);
  return next === wires ? b : { ...b, wires: next };
}

const one = <A extends { ui?: any; model?: any }>(s: A): A => {
  let out: any = s;
  const d = s?.model?.drawings?.["4"];
  if (d) { const p = page4(d); if (p !== d) out = { ...out, model: { ...out.model, drawings: { ...out.model.drawings, 4: p } } }; }
  if (s?.ui?.build) { const b = build(s.ui.build); if (b !== s.ui.build) out = { ...out, ui: { ...out.ui, build: b } }; }
  return out;
};
/** A saved CP-002 project with each pump's feedback through its delta contactor's closer, on page 4 and in the panel: its own work and
 *  each earlier attempt it keeps ("Start again", #1220). */
export function deltaFeedbackCp002Save<T extends { ui?: any; model?: any; attempts?: any[] }>(s: T): T {
  const out = one(s);
  return Array.isArray(s?.attempts) ? { ...out, attempts: s.attempts.map(a => one(a)) } : out;
}
